/** 脱敏日志及实际正文 Diff；复用审计对照样式，文本由 React 转义而不执行 HTML。 */
import type { LandingPageDto, LandingTextChange } from '@novel-admin/shared';
import { useState } from 'react';
import { useLandingDesensitizationDetail, useLandingDesensitizationLogs } from '~/data/queries';
import { fmtDateTime } from '~/lib/format';
import { landingAiProviderLabel } from '~/lib/landing-ai-provider-label';
import { Dialog } from '~/ui/Dialog';
import './LandingDesensitizationLogDialog.css';
const STATUS = { SUCCEEDED: '脱敏完成', NO_CHANGE: '无需修改', FAILED: '脱敏失败' };
function DiffLine({
  text,
  line,
  added,
}: { text: string | null; line: number | null; added: boolean }) {
  return (
    <div
      className={`landing-text-diff-line ${added ? 'landing-text-diff-added' : 'landing-text-diff-removed'}`}
    >
      <span className="mono muted">{line ?? ''}</span>
      <span className="mono" aria-hidden="true">
        {text === null ? '' : added ? '+' : '−'}
      </span>
      <code dir="auto">{text ?? ''}</code>
    </div>
  );
}
export function LandingTextDiff({
  changes,
  mode,
  labels = { before: '脱敏前', after: '脱敏后', description: '脱敏前后正文 Diff' },
}: {
  changes: LandingTextChange[];
  mode: 'split' | 'unified';
  labels?: { before: string; after: string; description: string };
}) {
  return (
    <div className="landing-text-diff" aria-label={labels.description}>
      {mode === 'split' && (
        <div className="landing-text-diff-pair landing-text-diff-heading">
          <span>{labels.before}</span>
          <span>{labels.after}</span>
        </div>
      )}
      {changes.map((change, index) => (
        <div
          key={`${change.before_line}-${change.after_line}-${index}`}
          className={mode === 'split' ? 'landing-text-diff-pair' : 'landing-text-diff-hunk'}
        >
          {mode === 'unified' && (
            <div className="mono muted landing-text-diff-heading">
              @@ 原文 {change.before_line ?? '新增'} → 新文 {change.after_line ?? '删除'} @@
            </div>
          )}
          {(mode === 'split' || change.before !== null) && (
            <DiffLine text={change.before} line={change.before_line} added={false} />
          )}
          {(mode === 'split' || change.after !== null) && (
            <DiffLine text={change.after} line={change.after_line} added />
          )}
        </div>
      ))}
    </div>
  );
}
export function LandingDesensitizationLogDialog({
  task,
  onClose,
}: { task: LandingPageDto; onClose: () => void }) {
  const logs = useLandingDesensitizationLogs(task.id, task.updated_at);
  const [selected, setSelected] = useState<string | null>(null);
  const [mode, setMode] = useState<'split' | 'unified'>('split');
  const chosen = selected ?? logs.data?.[0]?.id ?? null;
  const detail = useLandingDesensitizationDetail(task.id, chosen);
  return (
    <Dialog
      title="规则处理与 AI 脱敏日志"
      subtitle={task.task_id}
      width={1120}
      onClose={onClose}
      footer={
        <button type="button" className="btn" onClick={onClose}>
          关闭
        </button>
      }
    >
      <div className="vstack" style={{ padding: 18, gap: 14 }}>
        <p className="muted">
          对比每次处理前后的实际 HTML 正文。脱敏完成不代表广告平台审核通过；重新生成后需再次脱敏。
        </p>
        {logs.isPending ? (
          <p>加载脱敏日志…</p>
        ) : logs.isError ? (
          <p role="alert">{logs.error.message}</p>
        ) : !logs.data.length ? (
          <p>暂无脱敏日志</p>
        ) : (
          <>
            <div className="landing-desensitization-history">
              {logs.data.map((log) => (
                <button
                  key={log.id}
                  type="button"
                  className={`btn btn-ghost landing-desensitization-history-row ${chosen === log.id ? 'selected' : ''}`}
                  aria-pressed={chosen === log.id}
                  onClick={() => setSelected(log.id)}
                >
                  <span className="chip chip-info">
                    {log.processing_type === 'RULE' ? '规则处理' : 'AI 脱敏'}
                  </span>
                  <span className={`chip ${log.status === 'FAILED' ? 'chip-neg' : 'chip-pos'}`}>
                    {STATUS[log.status]}
                  </span>
                  <span className="mono muted">{fmtDateTime(log.created_at)}</span>
                  <span className="mono">
                    {log.processing_type === 'RULE'
                      ? '字符遮挡'
                      : log.model
                        ? `${landingAiProviderLabel(log.provider)} · ${log.model}`
                        : '模型未记录'}
                  </span>
                  {log.language && <span className="mono muted">{log.language.toUpperCase()}</span>}
                  <span className={log.is_current ? 'chip chip-info' : 'muted'}>
                    {log.is_current ? '当前版本' : '历史记录'}
                  </span>
                </button>
              ))}
            </div>
            {detail.isPending ? (
              <p>加载效果对比…</p>
            ) : detail.isError ? (
              <p role="alert">{detail.error.message}</p>
            ) : (
              detail.data && (
                <>
                  <div className="hstack" style={{ gap: 12, flexWrap: 'wrap' }}>
                    <strong>
                      {detail.data.log.processing_type === 'RULE' ? '规则处理' : 'AI 脱敏'} ·{' '}
                      {STATUS[detail.data.log.status]}
                    </strong>
                    {detail.data.log.word_count !== null && (
                      <span className="muted">
                        词 {detail.data.log.word_count} 项 · 句{' '}
                        {detail.data.log.phrase_count ?? '—'} 项
                      </span>
                    )}
                    {detail.data.diff_available && (
                      <span className="chip">{detail.data.changes.length} 处正文变更</span>
                    )}
                    <div className="seg" style={{ marginLeft: 'auto' }}>
                      <button
                        type="button"
                        className={mode === 'split' ? 'on' : ''}
                        aria-pressed={mode === 'split'}
                        onClick={() => setMode('split')}
                      >
                        左右对照
                      </button>
                      <button
                        type="button"
                        className={mode === 'unified' ? 'on' : ''}
                        aria-pressed={mode === 'unified'}
                        onClick={() => setMode('unified')}
                      >
                        统一 Diff
                      </button>
                    </div>
                  </div>
                  {detail.data.log.error && (
                    <p role="alert" style={{ color: 'var(--neg)' }}>
                      {detail.data.log.error}
                    </p>
                  )}
                  {detail.data.log.status !== 'FAILED' &&
                    (!detail.data.diff_available ? (
                      <p className="muted">历史原文件缺失或已变化，无法可靠还原 Diff。</p>
                    ) : !detail.data.changes.length ? (
                      <p className="muted">本次未修改正文内容。</p>
                    ) : (
                      <LandingTextDiff changes={detail.data.changes} mode={mode} />
                    ))}
                </>
              )
            )}
          </>
        )}
      </div>
    </Dialog>
  );
}
